import { PIECE_VALUES } from '@blind-kings/game-core';
import type { SpriteAnimState } from '../../../characters';
import type { TeamPalette } from '../../../player-colors';
import {
  UNTURNED_COLOR,
  getDefaultSkinIdForTeam,
  getPlayerSkinDef,
} from '../../../player-colors';

export type PieceTint = { fill: string; stroke: string };

export type PreloadTask = {
  pieceValue: number;
  packId: string;
  state: SpriteAnimState;
  tint: PieceTint;
};

/** Every piece that can appear on the board, unturned included. */
export const PRELOADABLE_PIECE_VALUES: readonly number[] = [
  PIECE_VALUES.Unturned,
  PIECE_VALUES.Peasant,
  PIECE_VALUES.Assassin,
  PIECE_VALUES.Knight,
  PIECE_VALUES.Cavalry,
  PIECE_VALUES.Mage,
  PIECE_VALUES.Hero,
  PIECE_VALUES.King,
];

/** Strips warmed for a live match, in the order a match tends to need them. */
export const MATCH_ANIM_STATES: readonly SpriteAnimState[] = [
  'resting',
  'revealing',
  'hover',
  'moving',
  'attacking',
  'dead',
];

export function paletteForTeam(
  teamIndex: number | undefined,
  teamPalettes?: Partial<Record<0 | 1, TeamPalette>>
): PieceTint {
  if (teamIndex === 0 || teamIndex === 1) {
    const palette = teamPalettes?.[teamIndex];
    if (palette) return { fill: palette.fill, stroke: palette.stroke };
    const fallback = getPlayerSkinDef(getDefaultSkinIdForTeam(teamIndex));
    return { fill: fallback.fill, stroke: fallback.stroke };
  }
  return { fill: UNTURNED_COLOR.fill, stroke: UNTURNED_COLOR.stroke };
}

export function spritePackForTeam(
  teamIndex: number | undefined,
  teamPalettes?: Partial<Record<0 | 1, TeamPalette>>
): string {
  if (teamIndex === 0 || teamIndex === 1) {
    return teamPalettes?.[teamIndex]?.spritePackId ?? 'classic';
  }
  return 'classic';
}

export function preloadTaskKey(task: PreloadTask): string {
  return `${task.packId}:${task.pieceValue}:${task.state}:${task.tint.fill}:${task.tint.stroke}`;
}

function dedupe(tasks: readonly PreloadTask[]): PreloadTask[] {
  const seen = new Set<string>();
  return tasks.filter((task) => {
    const key = preloadTaskKey(task);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Assets every screen needs before the first paint: the classic fallback pack
 * at rest, in both default team tints. Deliberately small — this blocks boot.
 */
export function corePreloadPlan(): PreloadTask[] {
  const tints = [paletteForTeam(0), paletteForTeam(1)];
  const tasks = PRELOADABLE_PIECE_VALUES.flatMap((pieceValue) =>
    tints.map((tint) => ({
      pieceValue,
      packId: 'classic',
      state: 'resting' as const,
      tint,
    }))
  );
  return dedupe(tasks);
}

/**
 * Everything the two seats in a match can render. Warmed in the background
 * during the reveal so the first attack never waits on a download.
 */
export function matchPreloadPlan(
  teamPalettes?: Partial<Record<0 | 1, TeamPalette>>
): PreloadTask[] {
  const seats = [0, 1] as const;
  const tasks = seats.flatMap((teamIndex) => {
    const packId = spritePackForTeam(teamIndex, teamPalettes);
    const tint = paletteForTeam(teamIndex, teamPalettes);
    return MATCH_ANIM_STATES.flatMap((state) =>
      PRELOADABLE_PIECE_VALUES.map((pieceValue) => ({
        pieceValue,
        packId,
        state,
        tint,
      }))
    );
  });
  return dedupe(tasks);
}
